import React, { useState, useEffect } from 'react';
import { 
  FaHome, FaBuilding, FaMapMarkerAlt, FaPlus, FaEdit, 
  FaTrash, FaStar, FaMap, FaTimes 
} from 'react-icons/fa';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './AddressBook.css';

const AddressBook = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  
  const [formData, setFormData] = useState({
    addressType: 'home',
    label: '',
    addressLine1: '',
    addressLine2: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    phone: '',
    isDefault: false,
    googleMapLocation: {
      latitude: null,
      longitude: null,
      formattedAddress: ''
    }
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const response = await api.get('/address-book');
      if (response.data.success) {
        setAddresses(response.data.addresses);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast.error('Failed to load addresses');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate pincode
    if (!/^\d{6}$/.test(formData.pincode)) {
      toast.error('Pincode must be 6 digits');
      return;
    }

    setLoading(true);
    try {
      if (editingAddress) {
        // Update existing address
        const response = await api.put(`/address-book/${editingAddress._id}`, formData);
        if (response.data.success) {
          toast.success('Address updated successfully!');
          fetchAddresses();
          resetForm();
        }
      } else {
        // Add new address
        const response = await api.post('/address-book', formData);
        if (response.data.success) {
          toast.success('Address added successfully!');
          fetchAddresses();
          resetForm();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.delete(`/address-book/${addressId}`);
      if (response.data.success) {
        toast.success('Address deleted successfully!');
        fetchAddresses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete address');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (addressId) => {
    setLoading(true);
    try {
      const response = await api.put(`/address-book/${addressId}/set-default`);
      if (response.data.success) {
        toast.success('Default address updated!');
        fetchAddresses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to set default address');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      addressType: address.addressType,
      label: address.label,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || '',
      landmark: address.landmark || '',
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      country: address.country || 'India',
      phone: address.phone || '',
      isDefault: address.isDefault,
      googleMapLocation: address.googleMapLocation || {
        latitude: null,
        longitude: null,
        formattedAddress: ''
      }
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      addressType: 'home',
      label: '',
      addressLine1: '',
      addressLine2: '',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      phone: '',
      isDefault: false,
      googleMapLocation: {
        latitude: null,
        longitude: null,
        formattedAddress: ''
      }
    });
    setEditingAddress(null);
    setShowForm(false);
  };

  const openMapPicker = () => {
    setShowMapPicker(true);
    // Initialize Google Maps (will be implemented in production)
    toast.info('Google Maps integration will be available in production');
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      googleMapLocation: {
        latitude: location.lat,
        longitude: location.lng,
        formattedAddress: location.address
      }
    }));
    setShowMapPicker(false);
    toast.success('Location added!');
  };

  const getAddressIcon = (type) => {
    switch (type) {
      case 'home': return <FaHome />;
      case 'shop': return <FaBuilding />;
      case 'office': return <FaBuilding />;
      case 'warehouse': return <FaBuilding />;
      default: return <FaMapMarkerAlt />;
    }
  };

  const IndianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  return (
    <div className="address-book-container">
      <div className="address-book-header">
        <div>
          <h2>📍 Address Book</h2>
          <p>Manage your delivery and shipping addresses</p>
        </div>
        {!showForm && (
          <button 
            className="btn btn-primary add-address-btn" 
            onClick={() => setShowForm(true)}
          >
            <FaPlus /> Add New Address
          </button>
        )}
      </div>

      {showForm && (
        <div className="address-form-card">
          <div className="form-card-header">
            <h3>{editingAddress ? 'Edit Address' : 'Add New Address'}</h3>
            <button className="close-btn" onClick={resetForm}>
              <FaTimes />
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Address Type *</label>
                <select
                  name="addressType"
                  className="form-control"
                  value={formData.addressType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="home">🏠 Home (Delivery Address)</option>
                  <option value="shop">🏪 Shop (Shipping Address)</option>
                  <option value="office">🏢 Office (Shipping Address)</option>
                  <option value="warehouse">🏭 Warehouse</option>
                  <option value="other">📍 Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Label / Name *</label>
                <input
                  type="text"
                  name="label"
                  className="form-control"
                  value={formData.label}
                  onChange={handleInputChange}
                  placeholder="e.g., My Home, Office, Shop Name"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Address Line 1 *</label>
              <input
                type="text"
                name="addressLine1"
                className="form-control"
                value={formData.addressLine1}
                onChange={handleInputChange}
                placeholder="House/Flat No., Building Name, Street"
                required
              />
            </div>

            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                name="addressLine2"
                className="form-control"
                value={formData.addressLine2}
                onChange={handleInputChange}
                placeholder="Area, Locality"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Landmark</label>
                <input
                  type="text"
                  name="landmark"
                  className="form-control"
                  value={formData.landmark}
                  onChange={handleInputChange}
                  placeholder="Near..."
                />
              </div>

              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  className="form-control"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>State *</label>
                <select
                  name="state"
                  className="form-control"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select State</option>
                  {IndianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Pincode * (6 digits)</label>
                <input
                  type="text"
                  name="pincode"
                  className="form-control"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  placeholder="123456"
                  pattern="\d{6}"
                  maxLength="6"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Contact Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Contact number for this address"
                />
              </div>

              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="country"
                  className="form-control"
                  value={formData.country}
                  onChange={handleInputChange}
                  readOnly
                />
              </div>
            </div>

            <div className="google-map-section">
              <label>
                <FaMap /> Google Maps Location (Optional)
              </label>
              {formData.googleMapLocation.latitude ? (
                <div className="location-preview">
                  <p>
                    📍 Location: {formData.googleMapLocation.latitude.toFixed(6)}, 
                    {formData.googleMapLocation.longitude.toFixed(6)}
                  </p>
                  {formData.googleMapLocation.formattedAddress && (
                    <p className="formatted-address">
                      {formData.googleMapLocation.formattedAddress}
                    </p>
                  )}
                  <button 
                    type="button" 
                    className="btn btn-secondary btn-sm"
                    onClick={openMapPicker}
                  >
                    Change Location
                  </button>
                </div>
              ) : (
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={openMapPicker}
                >
                  <FaMapMarkerAlt /> Add Location from Map
                </button>
              )}
              <small className="form-text">
                Adding a map location helps delivery partners find your address easily
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleInputChange}
                />
                <span>Set as default address</span>
              </label>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={resetForm}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading}
              >
                {loading ? 'Saving...' : (editingAddress ? 'Update Address' : 'Add Address')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="addresses-grid">
        {addresses.length === 0 ? (
          <div className="empty-state">
            <FaMapMarkerAlt className="empty-icon" />
            <h3>No addresses added yet</h3>
            <p>Add your home and shop addresses for easy delivery and shipping</p>
          </div>
        ) : (
          addresses.map(address => (
            <div key={address._id} className={`address-card ${address.isDefault ? 'default' : ''}`}>
              {address.isDefault && (
                <div className="default-badge">
                  <FaStar /> Default
                </div>
              )}
              
              <div className="address-header">
                <div className="address-type-icon">
                  {getAddressIcon(address.addressType)}
                </div>
                <div>
                  <h3>{address.label}</h3>
                  <span className="address-type-label">{address.addressType}</span>
                </div>
              </div>

              <div className="address-details">
                <p>{address.addressLine1}</p>
                {address.addressLine2 && <p>{address.addressLine2}</p>}
                {address.landmark && <p>Near {address.landmark}</p>}
                <p>{address.city}, {address.state}</p>
                <p><strong>Pincode:</strong> {address.pincode}</p>
                {address.phone && <p><strong>Phone:</strong> {address.phone}</p>}
                
                {address.googleMapLocation?.latitude && (
                  <div className="map-location-info">
                    <FaMapMarkerAlt /> Location attached
                  </div>
                )}
              </div>

              <div className="address-actions">
                {!address.isDefault && (
                  <button 
                    className="btn btn-link" 
                    onClick={() => handleSetDefault(address._id)}
                    disabled={loading}
                  >
                    <FaStar /> Set as Default
                  </button>
                )}
                <button 
                  className="btn btn-link" 
                  onClick={() => handleEdit(address)}
                  disabled={loading}
                >
                  <FaEdit /> Edit
                </button>
                <button 
                  className="btn btn-link btn-danger" 
                  onClick={() => handleDelete(address._id)}
                  disabled={loading}
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Google Maps Modal (Placeholder) */}
      {showMapPicker && (
        <div className="modal-overlay" onClick={() => setShowMapPicker(false)}>
          <div className="modal-content map-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaMap /> Select Location on Map</h3>
              <button className="close-btn" onClick={() => setShowMapPicker(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="map-placeholder">
                <FaMapMarkerAlt className="map-icon" />
                <p>Google Maps integration will be available in production</p>
                <p className="map-note">
                  You'll be able to:
                  <br />• Search for your address
                  <br />• Pin exact location on map
                  <br />• Auto-fill address from map
                </p>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    // Demo: Add mock location
                    handleLocationSelect({
                      lat: 28.6139,
                      lng: 77.2090,
                      address: 'Selected location will appear here'
                    });
                  }}
                >
                  Use Demo Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressBook;
